import React, { useEffect, useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Play, ExternalLink, Image as ImageIcon, Loader2 } from "lucide-react";
import { fetchPublicPortfolioCategories, fetchPublicPortfolioItems } from "@/lib/publicApi";

const PortfolioSection = () => {
  const [activeFilter, setActiveFilter] = useState("all");
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    Promise.all([fetchPublicPortfolioItems(), fetchPublicPortfolioCategories()])
      .then(([items, cats]) => {
        if (!isMounted) return;
        setPortfolioItems(items);
        setCategories(cats);
      })
      .catch(() => {
        if (isMounted) setError(true);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const filters = [
    { key: "all", label: "Semua Project" },
    ...categories.map((category) => ({ key: category.slug, label: category.name })),
  ];

  const filteredItems =
    activeFilter === "all"
      ? portfolioItems
      : portfolioItems.filter((item) => item.category?.slug === activeFilter);

  return (
    <section id="portfolio" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="container mx-auto px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">
              Portfolio
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Karya Terbaik <span className="text-gradient-blue-orange">Kami</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Lihat berbagai project yang telah kami selesaikan untuk klien-klien ternama dengan standar kualitas tinggi.
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center gap-2 py-20 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin" /> Memuat portfolio...
            </div>
          ) : error ? (
            <div className="text-center py-20 text-gray-500">
              Gagal memuat data portfolio. Silakan coba lagi nanti.
            </div>
          ) : (
            <>
              {/* Filter Buttons */}
              <div className="flex flex-wrap justify-center gap-4 mb-12">
                {filters.map((filter) => (
                  <Button
                    key={filter.key}
                    variant={activeFilter === filter.key ? "default" : "outline"}
                    onClick={() => setActiveFilter(filter.key)}
                    className={`px-6 py-2 rounded-full transition-all duration-300 ${
                      activeFilter === filter.key
                        ? "bg-blue-600 hover:bg-blue-700 text-white"
                        : "hover:bg-blue-50 hover:border-blue-300"
                    }`}
                  >
                    {filter.label}
                  </Button>
                ))}
              </div>

              {/* Portfolio Grid with Animation */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-12">
                {filteredItems.map((item, index) => (
                  <Card
                    key={item.id}
                    className="group overflow-hidden border-0 bg-white shadow-lg hover-lift animate-fade-in"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div className="relative overflow-hidden">
                      <img
                        src={item.image_url}
                        alt={item.title}
                        className="w-full h-48 sm:h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                      />
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4 px-4">
                          <Button size="sm" className="bg-white/20 hover:bg-white/30 backdrop-blur-md border border-white/30 text-white hover:text-white">
                            <Play className="w-4 h-4 mr-2" />
                            Preview
                          </Button>
                          <Button size="sm" variant="outline" className="bg-white/20 hover:bg-white/30 backdrop-blur-md border border-white/30 text-white hover:text-white">
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Detail
                          </Button>
                        </div>
                      </div>
                    </div>
                    <CardContent className="p-4 sm:p-6">
                      {item.type && (
                        <div className="flex items-center justify-between mb-3">
                          <Badge variant="secondary" className="bg-blue-100 text-blue-700 text-xs sm:text-sm">
                            {item.type}
                          </Badge>
                        </div>
                      )}
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                      {item.description && (
                        <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* No Results State */}
              {filteredItems.length === 0 && (
                <div className="text-center py-12">
                  <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <ImageIcon className="w-12 h-12 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">Tidak ada project ditemukan</h3>
                  <p className="text-gray-500">Coba pilih kategori lain atau lihat semua project</p>
                  <Button
                    onClick={() => setActiveFilter("all")}
                    className="mt-4 bg-blue-600 hover:bg-blue-700"
                  >
                    Lihat Semua Project
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default PortfolioSection;
